import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ClipboardList, ArrowRight, GraduationCap } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#101876", "#329112", "#f59e0b", "#e11d48", "#8b5cf6", "#06b6d4"];

export default function OrgCourseDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/org/course-dashboard");
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <DashboardLayout title="Course Overview"><LoadingBlock label="Loading course dashboard…" /></DashboardLayout>;
  if (!data) return null;
  const t = data.totals;

  return (
    <DashboardLayout title="Course Overview" breadcrumbs={["My Institute", "Courses"]}>
      <div className="space-y-6" data-testid="org-course-dashboard">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm text-slate-600">Courses mapped to your organization by Steward Super Admin.</div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/app/hospital/courses" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]">
              <BookOpen className="h-3.5 w-3.5" /> Mapped courses
            </Link>
            <Link to="/app/hospital/courses/bulk-assign" className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="org-bulk-assign-btn">
              <ClipboardList className="h-3.5 w-3.5" /> Bulk assign
            </Link>
          </div>
        </div>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Mapped Courses" value={t.mapped_courses} icon="BookOpen" tone="navy" testId="org-kpi-mapped" index={0} />
          <StatCard label="Mandatory" value={t.mandatory_courses} icon="AlertTriangle" tone="amber" testId="org-kpi-mandatory" index={1} />
          <StatCard label="Optional" value={t.optional_courses} icon="Star" tone="slate" testId="org-kpi-optional" index={2} />
          <StatCard label="Assignments" value={t.total_assignments} icon="ClipboardList" tone="green" testId="org-kpi-assignments" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Assigned staff" value={t.assigned_staff} icon="UserCheck" tone="navy" testId="org-kpi-assignedstaff" index={4} />
          <StatCard label="Pending staff" value={t.pending_staff} icon="UserX" tone="amber" testId="org-kpi-pending" index={5} />
          <StatCard label="Expired mappings" value={t.expired_mappings} icon="Clock" tone="slate" testId="org-kpi-expired" index={6} />
          <StatCard label="Overdue (Phase 6)" value={0} icon="AlarmClock" tone="slate" testId="org-kpi-overdue" index={7} />
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Assignment split</div>
            <h3 className="text-lg font-bold text-slate-900">Course assignments per course</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.per_course.slice(0, 8)}>
                  <XAxis dataKey="course_title" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#101876" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Departments</div>
            <h3 className="text-lg font-bold text-slate-900">Assignments per department</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.per_department.slice(0, 8)} dataKey="count" nameKey="department_name" innerRadius={30} outerRadius={70}>
                    {data.per_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Status distribution</div>
          <h3 className="text-lg font-bold text-slate-900">Assignment statuses</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-6">
            {data.status_stats.map((s) => (
              <div key={s.status} className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-center">
                <div className="text-2xl font-black text-slate-900">{s.count}</div>
                <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{s.status.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
